import { Box, Text } from '@bechtel/gds';
import { ToolCategory } from '@bechtel/ht-types';
import { orderBy, sumBy } from 'lodash';
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { AlphabetList } from '../alphabet-list';
import { allCategoryItem } from './form-data';
import {
  CategoryItemStyled,
  CategoryListStyled,
  NonCatalogItemStyled,
  ToolCategoryIcon,
  ToolCategorySectionHeaderStyled,
} from './styled';
import {
  CategoryGroupSelectionAction,
  CategoryGroupSelectionState,
  ToolCategoryItemProps,
  ToolCategoryProps,
} from './types';

export const ToolCategoryItem: React.FC<ToolCategoryItemProps> = ({ toolCategory, onItemSelected, isSelected }) => {
  return (
    <CategoryItemStyled
      isSelected={isSelected}
      onClick={() => {
        onItemSelected(toolCategory);
      }}
    >
      <Box px={{ sm: '500' }} py={{ sm: '400' }} alignItems={{ sm: 'center' }}>
        <ToolCategoryIcon
          className={`icon-tools-consumables icon-${toolCategory.name.toLowerCase()}`}
        ></ToolCategoryIcon>
        <Box ml={{ sm: '500' }} flexDirection={{ sm: 'column' }} style={{ gap: '6px', marginLeft: '8px' }}>
          <Text size="400" weight="600" color="blue.800" family={'openSansSemiBold'}>
            {toolCategory.name}
          </Text>
          {/* <Box>
            <Text size="300" family={'openSansCondensed'}>
              Consumables:
            </Text>
            <Text size="300" color="grey.400" style={{ marginLeft: '4px' }} family={'openSansCondensed'}>
              {toolCategory.toolCategoryStats?.consumablesCount}
            </Text>
            &nbsp;&nbsp;
            <Text size="300" family={'openSansCondensed'}>
              Tools:
            </Text>
            <Text size="300" color="grey.400" style={{ marginLeft: '4px' }} family={'openSansCondensed'}>
              {toolCategory.toolCategoryStats?.toolsCount}
            </Text>
          </Box> */}
        </Box>
      </Box>
    </CategoryItemStyled>
  );
};

const highlightedSectionReducer = (state: CategoryGroupSelectionState, action: CategoryGroupSelectionAction): any => {
  switch (action.type) {
    case 'select':
      return { alphabet: action.payload, shouldScrollToTop: true };
    case 'hover':
      return { alphabet: action.payload, shouldScrollToTop: false };
    default:
      return state;
  }
};

export const ToolCategoryList: React.FC<ToolCategoryProps> = ({
  onCategorySelected,
  toolCategories,
  onNonToolCatalogSelected,
}) => {
  const [selectedCategoryName, setSelectedCategoryName] = useState(allCategoryItem.name);
  const [hightlightedSection, dispatch] = useReducer(highlightedSectionReducer, {
    alphabet: 'A',
    shouldScrollToTop: false,
  });
  const listRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement[]>([]);
  const onItemSelected = (toolCategory) => {
    setSelectedCategoryName(toolCategory.name);
    onCategorySelected(toolCategory);
  };

  const toolCategoryStats = useMemo(() => {
    return {
      consumablesCount: sumBy(toolCategories, (category) => {
        return category.toolCategoryStats?.consumablesCount || 0;
      }),
      toolsCount: sumBy(toolCategories, (category) => {
        return category.toolCategoryStats?.toolsCount || 0;
      }),
    };
  }, [toolCategories]);

  const groupedToolCategories: Record<string, ToolCategory[]> = useMemo(() => {
    const sortedToolCategories = orderBy(toolCategories, 'name', 'asc');
    return sortedToolCategories.reduce((acc, toolCategory) => {
      const firstLetter = toolCategory.name.charAt(0);
      if (!acc[firstLetter]) {
        acc[firstLetter] = [];
      }
      acc[firstLetter].push(toolCategory);
      return acc;
    }, {});
  }, [toolCategories]);

  useEffect(() => {
    if (hightlightedSection.shouldScrollToTop) {
      const key = Object.keys(groupedToolCategories).findIndex((key) => key === hightlightedSection.alphabet) || 0;
      if (key < 0) return;
      listRef?.current?.scrollTo({
        top: itemsRef.current[key].offsetTop - (itemsRef.current[0].parentElement?.offsetTop || 0),
        behavior: 'smooth',
      });
    }
  }, [hightlightedSection, groupedToolCategories]);

  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, Object.keys(groupedToolCategories).length);
  }, [groupedToolCategories]);

  return (
    <div>
      <Box style={{ height: 'calc(100vh - 111px)' }}>
        <AlphabetList
          selectedAlphabet={hightlightedSection.alphabet}
          onSelect={(alphabet) => {
            dispatch({ type: 'select', payload: alphabet });
          }}
        ></AlphabetList>
        <Box flexDirection={{ sm: 'column' }}>
          <CategoryListStyled ref={listRef}>
            <Box flexDirection={{ sm: 'column' }}>
              <ToolCategoryItem
                key={allCategoryItem.name}
                toolCategory={{
                  ...allCategoryItem,
                  toolCategoryStats: toolCategoryStats,
                }}
                onItemSelected={(AllCategoryItem) => {
                  onItemSelected(AllCategoryItem);
                }}
                isSelected={allCategoryItem.name == selectedCategoryName}
              />
              {Object.entries(groupedToolCategories).map(([key, toolCategories], index) => {
                return (
                  <div
                    key={key}
                    ref={(element) => {
                      if (itemsRef?.current && element) itemsRef.current[index] = element;
                    }}
                    onMouseEnter={() => {
                      dispatch({ type: 'hover', payload: key });
                    }}
                  >
                    <ToolCategorySectionHeaderStyled>
                      <Text size="400" weight="600" color="blue.800">
                        {key}
                      </Text>
                    </ToolCategorySectionHeaderStyled>
                    {toolCategories?.map((toolCategory) => (
                      <ToolCategoryItem
                        key={toolCategory.name}
                        toolCategory={toolCategory}
                        onItemSelected={(toolCategory) => {
                          onItemSelected(toolCategory);
                        }}
                        isSelected={toolCategory.name === selectedCategoryName}
                      />
                    ))}
                  </div>
                );
              })}
            </Box>
          </CategoryListStyled>
          <NonCatalogItemStyled onClick={onNonToolCatalogSelected}>
            <Text color="purple.500" size="400" weight="600">
              Request non-catalog-item
            </Text>
          </NonCatalogItemStyled>
        </Box>
      </Box>
    </div>
  );
};
